import type { DeepString } from "@/lib/i18n";

/**
 * CShop - polskie tlumaczenia. Krok 1 zamawiania (pelna strona koszyka).
 * Baseline mapowany z legacy `c_shop_pl.lang` (klucze koszyka + platnosc +
 * dostawa + kod rabatowy).
 */
export const pl = {
  cart: {
    title: "Koszyk",
    empty:
      "Twój koszyk jest pusty, wprowadź produkty do koszyka by je zamówić.",
    productId: "Numer produktu",
    productCategory: "Kategoria",
    productVariant: "Wariant",
    productCount: "Ilość",
    productPrice: "Cena brutto",
    productPriceBeforePromotion: "Cena brutto przed promocją",
    productSum: "Suma brutto",
    variantColorAlt: "Próbka koloru",
    summary: "Podsumowanie",
    pricesIncludeVat: "Wszystkie kwoty są brutto (zawierają VAT).",
    sumCount: "Ilość",
    sumProducts: "Produkty",
    sumDelivery: "Dostawa",
    discount: "Zniżka",
    total: "Razem (brutto)",
    continueShopping: "Kontynuuj zakupy",
    gotoOrder: "Przejdź do zamawiania",
    countLabel: "Ilość",
    countDecrement: "Zmniejsz ilość",
    countIncrement: "Zwiększ ilość",
    deleteItem: "Usuń pozycję z koszyka",
    updateFailed: "Nie udało się zaktualizować ilości. Spróbuj ponownie.",
    deleteFailed: "Nie udało się usunąć pozycji. Spróbuj ponownie.",
    gotoOrderDisabledNoPayment: "Wybierz opcję płatności powyżej, aby przejść do zamówienia.",
    gotoOrderDisabledNoDelivery: "Wybierz opcję dostawy powyżej, aby przejść do zamówienia.",
    gotoOrderDisabledStock: "Któraś z pozycji przekracza stan magazynowy - popraw ilość, aby kontynuować.",
  },
  payment: {
    heading: "Opcje płatności",
    emptyList:
      "Brak dostępnych opcji płatności — skontaktuj się z administratorem sklepu.",
    changeFailed: "Nie udało się zmienić opcji płatności. Spróbuj ponownie.",
    /** Tooltip przy wyszarzonej opcji płatności online, gdy suma koszyka
        przekracza `shop_online_payment_max_amount` (per waluta). `{amount}` +
        `{currency}` podstawiane runtime. */
    disabledOverMax:
      "Płatność online niedostępna dla zamówień powyżej {amount} {currency}. Wybierz przelew tradycyjny.",
    /** Pod przyciskiem "Przejdź do zamawiania" - hint gdy klient wybrał
        wcześniej płatność online, a po zmianie ilości koszyk przekroczył próg. */
    gotoOrderDisabledOverMax:
      "Wybrana płatność online jest niedostępna dla tej kwoty - wybierz inną opcję.",
  },
  delivery: {
    heading: "Opcje dostawy",
    pickPaymentFirst: "Najpierw wybierz opcję płatności.",
    emptyList:
      "Brak opcji dostawy dla wybranej płatności — skontaktuj się z administratorem sklepu.",
    changeFailed: "Nie udało się zmienić opcji dostawy. Spróbuj ponownie.",
    free: "bezpłatnie",
  },
  discount: {
    heading: "Kod rabatowy",
    placeholder: "Wpisz kod rabatowy",
    apply: "Sprawdź",
    remove: "Usuń kod",
    hint: "Zniżka dotyczy produktów nieobjętych promocją.",
    active: "Kod aktywny",
    statusEmpty: "Wpisz kod rabatowy.",
    statusOk: "Kod prawidłowy.",
    statusNotExists: "Podany kod nie istnieje.",
    statusExpired: "Kod stracił ważność.",
    statusUsed: "Kod został już wykorzystany.",
    statusExhausted: "Kod osiągnął limit użyć.",
    checkFailed: "Nie udało się sprawdzić kodu. Spróbuj ponownie.",
  },
  freeDelivery: {
    /** `{price} {currency}` - kwota progu darmowej dostawy. */
    threshold: "Zamów za {price} {currency} lub więcej, a dostawa jest gratis.",
    is: "Zdobywasz darmową dostawę.",
    /** `{remaining} {currency}` - kwota do przekroczenia progu. */
    remaining: "Do darmowej dostawy brakuje {remaining} {currency}.",
  },
  order: {
    title: "Zamówienie",
    /** Sekcje wewnątrz formularza. */
    sectionContact: "Dane kontaktowe",
    sectionSendAddress: "Adres wysyłki",
    sectionFvatData: "Dane do faktury",
    sectionCheckboxData: "Oświadczenia",
    /** Etykiety pól. */
    labelEmail: "Adres E-mail",
    labelName: "Imię",
    labelSurname: "Nazwisko",
    labelTelephone: "Telefon",
    labelCity: "Miejscowość",
    labelPostCode: "Kod pocztowy",
    labelStreet: "Ulica",
    labelNr: "Nr",
    labelPass: "Hasło",
    labelPPass: "Powtórz hasło",
    labelCompanyName: "Nazwa firmy lub Imię Nazwisko",
    labelNip: "NIP",
    labelCountry: "Kraj",
    labelBillingCountry: "Kraj (faktura)",
    countrySelectPlaceholder: "Wybierz kraj",
    /** Checkbox faktury (włącza sekcję "Dane do faktury"). */
    fvatCheckbox: "Proszę o wystawienie faktury VAT",
    /** Checkboxy zgód. `{regulamin_address}` i `{host}` - zamieniane runtime. */
    regulaminCheckbox:
      "Oświadczam, że zapoznałem(am) się z [regulaminem]({regulamin_address}) serwisu {host} i akceptuję wszystkie jego postanowienia.",
    personalDataCheckbox:
      "Wyrażam zgodę na przetwarzanie moich danych osobowych zgodnie z ustawą z dn. 29 sierpnia 1997 r. o ochronie danych osobowych.",
    marketingCheckbox:
      "Wyrażam zgodę na wysyłanie elektronicznej informacji handlowej na mój adres email przez serwis {host}.",
    /** Zakładki dla niezalogowanego. */
    tabNewClient: "Nowy klient",
    tabJustOrder: "Zamówienie bez rejestracji",
    tabLogin: "Zamów jako zalogowany",
    /** Panel logowania. */
    loginPromptTitle: "Zaloguj się",
    loginPromptHint:
      "Zaloguj się, aby złożyć zamówienie z danymi ze swojego konta. Po zalogowaniu wrócisz do formularza.",
    loginLabelLogin: "E-mail lub login",
    loginLabelPassword: "Hasło",
    loginSubmit: "Zaloguj się",
    loginSubmitBusy: "Logowanie…",
    loginFailed: "Logowanie nie powiodło się. Sprawdź dane i spróbuj ponownie.",
    /** Puste sloty na komunikaty. */
    empty: "Twój koszyk jest pusty. Wróć do sklepu, aby dodać produkty.",
    backToCard: "Wróć do koszyka",
    submit: "Zamów i płacę",
    submitBusy: "Wysyłanie…",
    /** Komunikaty. */
    topError: "Nie udało się złożyć zamówienia. Spróbuj ponownie.",
    /** Walidacja klient-side. `{field}` = etykieta pola (podstawiane runtime). */
    validationRequired: 'Pole "{field}" jest wymagane.',
    validationEmail: "Niepoprawny adres email.",
    validationTelephone: "Niepoprawny numer telefonu.",
    validationPassMinLength: "Hasło musi mieć min. 8 znaków.",
    validationPassMismatch: "Powtórzone hasło nie zgadza się.",
    validationRegulamin: "Należy zaakceptować regulamin.",
    validationPersonalData: "Należy wyrazić zgodę na przetwarzanie danych osobowych.",
    /** Summary label. */
    summaryHeading: "Twoje zamówienie",
  },
  inpost: {
    heading: "Paczkomat InPost",
    /** Renderowany pod dostawa, gdy nie wybrano paczkomatu. */
    noneSelected: "Nie wybrano jeszcze paczkomatu.",
    /** Podpowiedz walidacyjna - gdy user chce przejsc do kroku 2, a inpost dostawa
        wymaga wyboru punktu. */
    gotoOrderRequired: "Wybierz paczkomat InPost, aby przejść do zamawiania.",
    /** Buttony przy widoku wybranego paczkomatu. */
    change: "Zmień paczkomat",
    remove: "Usuń wybór",
    select: "Wybierz paczkomat",
    /** Nagłówek modala + label opisu. */
    modalTitle: "Wybór paczkomatu InPost",
    modalClose: "Zamknij",
    /** Input autocomplete. */
    searchPlaceholder: "Wpisz numer paczkomatu, miejscowość, ulicę lub kod pocztowy",
    searchAriaLabel: "Wyszukaj paczkomat",
    searchHint: "Wpisz frazę powyżej, aby wyszukać, lub kliknij marker na mapie.",
    searchLoading: "Wyszukiwanie…",
    searchNoResults: "Brak wyników dla podanej frazy.",
    /** Button "znajdz najblizszy". */
    nearest: "Znajdź najbliższy paczkomat",
    nearestBusy: "Ustalanie lokalizacji…",
    /** Bledy geolokalizacji / API. */
    geolocationDenied: "Odmówiono dostępu do lokalizacji. Wpisz miejscowość w wyszukiwarce.",
    geolocationUnsupported: "Twoja przeglądarka nie obsługuje geolokalizacji.",
    nearestNotFound: "Nie znaleziono paczkomatu w pobliżu podanej lokalizacji.",
    loadFailed: "Nie udało się załadować danych paczkomatów. Spróbuj ponownie.",
    saveFailed: "Nie udało się zapisać wybranego paczkomatu. Spróbuj ponownie.",
    mapFailed: "Nie udało się załadować mapy. Użyj wyszukiwania po nazwie / adresie.",
    /** Info gdy Google Maps API nie jest skonfigurowane w domenie (brak klucza). */
    mapUnavailable: "Mapa niedostępna - skorzystaj z wyszukiwarki paczkomatów poniżej.",
    /** Akcje w modalu. */
    confirm: "Zatwierdź wybór",
    cancel: "Anuluj",
    /** Etykiety dla screen readerow. */
    listResultsAriaLabel: "Wyniki wyszukiwania paczkomatów",
    /** Sticky karta na gorze lewego panelu z aktualnie wybranym paczkomatem. */
    selectedHeading: "Wybrany paczkomat",
    /** Aria-label buttona X kasujacego wybor przy sticky karcie. */
    selectedClear: "Wyczyść wybór",
  },
  pickupPoint: {
    heading: "Punkt odbioru",
    noneSelected: "Nie wybrano jeszcze punktu odbioru.",
    gotoOrderRequired: "Wybierz punkt odbioru, aby przejść do zamawiania.",
    change: "Zmień punkt",
    remove: "Usuń wybór",
    select: "Wybierz punkt odbioru",
    modalTitle: "Wybór punktu odbioru",
    modalClose: "Zamknij",
    searchPlaceholder: "Wpisz miasto, nazwę lub kod pocztowy",
    searchAriaLabel: "Filtruj punkty odbioru",
    emptyList: "Brak dostępnych punktów odbioru dla tej opcji dostawy.",
    noSearchMatch: "Żaden punkt nie pasuje do wyszukiwania.",
    loading: "Ładowanie…",
    loadFailed: "Nie udało się załadować listy punktów. Spróbuj ponownie.",
    saveFailed: "Nie udało się zapisać wybranego punktu. Spróbuj ponownie.",
    confirm: "Zatwierdź wybór",
    cancel: "Anuluj",
    navigate: "Nawiguj w Google Maps",
    openHoursHeading: "Godziny otwarcia",
    openHoursMonFri: "Pn-Pt",
    openHoursSat: "Sobota",
    openHoursSun: "Niedziela",
    openHoursClosed: "Nieczynne",
    openHoursAllDay: "Całą dobę",
    listResultsAriaLabel: "Lista dostępnych punktów odbioru",
    selectedClear: "Wyczyść wybór",
  },
  orderComplete: {
    /** `{nr}` = numer zamowienia. Naglowek "dziekujemy" po sukcesie. */
    heading: "Twoje zamówienie o numerze {nr} zostało przyjęte do realizacji, dziękujemy!",
    /** `{amount}` = kwota + waluta ("129,90 zł"). Kwota jest brutto (zawiera VAT). */
    toPaySum: "Suma zamówienia do zapłaty (brutto): {amount}",
    /** `{nr}` = numer zamowienia. */
    orderNr: "Twój numer zamówienia to {nr}",
    /** Info banner gdy zamowienie oplacone. */
    paidBanner: "Zamówienie zostało opłacone.",
    /** Buttony ponizej podsumowania. */
    yourOrders: "Twoje zamówienia",
    goToPayment: "Zapłać teraz - online",
    /** Komunikat gdy `order === null` (nieprawidlowy id lub obce zamowienie). */
    notFound: "Nie znaleziono zamówienia. Sprawdź link z maila lub zaloguj się i przejdź do listy zamówień.",
    /** Etykiety bloku `payment_info` wg `type` z m_shop_payment_options
        (backend `getPaymentInfo`). Sama tresc instrukcji (np. numer konta)
        idzie pod etykieta z `content.payment_info.info`. */
    paymentInfoTransfer: "Płatności można dokonać na numer konta bankowego:",
    paymentInfoOnline: "Płatności można dokonać poprzez internetowy system płatności dostępny w naszym serwisie",
    paymentInfoOnlineWithAccount: "Płatności można dokonać poprzez internetowy system płatności dostępny w naszym serwisie lub na nr konta bankowego:",
    paymentInfoCash: "Płatności można dokonać gotówką w punktach sprzedaży:",
  },
  /** `formatPrice(129.9, "zł")` -> "129,90 zł". Puste currency -> tylko liczba. */
  formatPrice: (value: number, currency: string): string => {
    const rounded = (Math.round(value * 100) / 100).toFixed(2).replace(".", ",");
    return currency ? `${rounded} ${currency}` : rounded;
  },
} as const;

export type CShopTranslations = DeepString<typeof pl>;
